import {
  Alert,
  AlertIcon,
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import {
  type ChangeEvent,
  type FC,
  useCallback,
  useRef,
  useState,
} from 'react';

import {
  type AddBulkUsersResult,
  addBulkUsers,
  type BulkUserPayload,
  MAX_BULK_USERS,
} from './admin-bulk-users.api';
import { parseBulkUserCsv } from './bulk-user-csv';

interface AdminBulkUsersModalProps {
  clientName: string;
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
}

const getErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : 'An unknown error occurred.';

const AdminBulkUsersModal: FC<AdminBulkUsersModalProps> = ({
  clientName,
  isOpen,
  onClose,
  onComplete,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [users, setUsers] = useState<BulkUserPayload[]>([]);
  const [fileName, setFileName] = useState('');
  const [fileError, setFileError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [isParsing, setIsParsing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<AddBulkUsersResult | null>(null);

  const reset = useCallback(() => {
    setUsers([]);
    setFileName('');
    setFileError('');
    setSubmitError('');
    setIsParsing(false);
    setIsSubmitting(false);
    setResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const closeModal = () => {
    if (isSubmitting) {
      return;
    }

    reset();
    onClose();
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setUsers([]);
    setResult(null);
    setSubmitError('');
    setFileError('');
    setFileName(file?.name ?? '');

    if (!file) {
      return;
    }

    setIsParsing(true);
    try {
      setUsers(await parseBulkUserCsv(file, clientName));
    } catch (error: unknown) {
      setFileError(getErrorMessage(error));
    } finally {
      setIsParsing(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitError('');
    setIsSubmitting(true);
    try {
      const bulkResult = await addBulkUsers(users);
      setResult(bulkResult);
      onComplete();
    } catch (error: unknown) {
      setSubmitError(getErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={closeModal}
      size="4xl"
      isCentered
      closeOnEsc={!isSubmitting}
      closeOnOverlayClick={!isSubmitting}
    >
      <ModalOverlay />
      <ModalContent maxHeight="90vh">
        <ModalHeader>Add Bulk Users to {clientName}</ModalHeader>
        <ModalCloseButton isDisabled={isSubmitting} />
        <ModalBody overflowY="auto">
          <Stack spacing="5">
            <Alert status="info">
              <AlertIcon />
              <Text>
                The CSV must contain the headers{' '}
                <Text as="span" fontWeight="bold">
                  name
                </Text>
                ,{' '}
                <Text as="span" fontWeight="bold">
                  email
                </Text>
                , and{' '}
                <Text as="span" fontWeight="bold">
                  password
                </Text>
                . Header labels are case-insensitive. Each file can contain up
                to {MAX_BULK_USERS.toLocaleString('en-US')} user records.
              </Text>
            </Alert>

            <FormControl isInvalid={Boolean(fileError)}>
              <FormLabel htmlFor="bulk-users-csv">CSV file</FormLabel>
              <Input
                ref={fileInputRef}
                id="bulk-users-csv"
                type="file"
                accept=".csv,text/csv"
                onChange={handleFileChange}
                isDisabled={isParsing || isSubmitting}
                paddingTop="1"
              />
              {fileError && <FormErrorMessage>{fileError}</FormErrorMessage>}
            </FormControl>

            {isParsing && (
              <Stack direction="row" align="center">
                <Spinner size="sm" />
                <Text>Reading CSV...</Text>
              </Stack>
            )}

            {users.length > 0 && !result && (
              <Box>
                <Text fontWeight="semibold" marginBottom="2">
                  {users.length} {users.length === 1 ? 'user' : 'users'} ready
                  from {fileName}
                </Text>
                <Box maxHeight="240px" overflowY="auto" borderWidth="1px">
                  <Table size="sm">
                    <Thead position="sticky" top="0" background="white">
                      <Tr>
                        <Th>Name</Th>
                        <Th>Email</Th>
                        <Th>Password</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {users.map((user, index) => (
                        <Tr key={`${user.email}-${index}`}>
                          <Td>{user.name}</Td>
                          <Td>{user.email}</Td>
                          <Td>{user.password.replace(/./g, '*')}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              </Box>
            )}

            {isSubmitting && (
              <Alert status="info">
                <AlertIcon />
                Creating {users.length} users. Keep this window open until
                processing finishes.
              </Alert>
            )}

            {submitError && (
              <Alert status="error">
                <AlertIcon />
                {submitError}
              </Alert>
            )}

            {result && (
              <Stack spacing="3">
                <Alert status={result.failed === 0 ? 'success' : 'warning'}>
                  <AlertIcon />
                  Added {result.succeeded} of {result.total} users.
                  {result.failed > 0
                    ? ` ${result.failed} users failed and are listed below.`
                    : ''}
                </Alert>

                {result.failures.length > 0 && (
                  <Box maxHeight="300px" overflowY="auto" borderWidth="1px">
                    <Table size="sm">
                      <Thead position="sticky" top="0" background="white">
                        <Tr>
                          <Th>Record</Th>
                          <Th>Name</Th>
                          <Th>Email</Th>
                          <Th>Failure reason</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {result.failures.map((failure) => (
                          <Tr key={`${failure.recordNumber}-${failure.email}`}>
                            <Td>{failure.recordNumber}</Td>
                            <Td>{failure.name}</Td>
                            <Td>{failure.email}</Td>
                            <Td>{failure.reason}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                )}
              </Stack>
            )}
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="ghost"
            marginRight="3"
            onClick={closeModal}
            isDisabled={isSubmitting}
          >
            {result ? 'Close' : 'Cancel'}
          </Button>
          {!result && (
            <Button
              colorScheme="green"
              onClick={handleSubmit}
              isLoading={isSubmitting}
              loadingText="Adding users"
              isDisabled={
                users.length === 0 ||
                isParsing ||
                isSubmitting ||
                Boolean(fileError)
              }
            >
              Add Bulk Users
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AdminBulkUsersModal;
